import { useEffect, useState } from "react";
import { Cpu, Server, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errorText, fmtTime } from "@/lib/api";
import { AddDeviceDialog, KeyReveal } from "@/components/doorman/AddDeviceDialog";

const isOnline = (iso) => iso && Date.now() - new Date(iso).getTime() < 3 * 60 * 1000;

export const DevicesPanel = ({ tick, onChange }) => {
  const [devices, setDevices] = useState([]);
  const [revealed, setRevealed] = useState(null);

  useEffect(() => {
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => {});
  }, [tick]);

  const rotate = async (d) => {
    try {
      const { data } = await api.post(`/devices/${d.id}/rotate`);
      setRevealed({ name: d.name, key: data.key });
      onChange();
    } catch (e) { toast.error(errorText(e)); }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/devices/${d.id}`);
      toast.success(`${d.name} removed — its key no longer works`);
      onChange();
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-400 max-w-2xl">Each DS-1 sits at the business's network gateway and runs the Agent List sensor. It catalogs AI tools used inside the building and AI agents reaching on-site systems, checks origins on the device, and sends only the agent, the network area and the time here — never devices, guests, IP addresses or personal data.</p>
        <AddDeviceDialog onCreated={onChange} />
      </div>
      {revealed && <KeyReveal name={revealed.name} deviceKey={revealed.key} onDone={() => setRevealed(null)} />}
      {devices.length === 0 && <p className="text-slate-500 text-sm py-12 text-center border border-dashed border-[#222D3D] rounded-md" data-testid="devices-empty">No devices yet. Add your first DS-1 or server.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {devices.map((d) => <DeviceCard key={d.id} d={d} onRotate={() => rotate(d)} onRemove={() => remove(d)} />)}
      </div>
    </section>
  );
};

const DeviceCard = ({ d, onRotate, onRemove }) => {
  const online = isOnline(d.last_seen_at);
  const Icon = d.hardware === "ds1" ? Cpu : Server;
  return (
    <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-5 space-y-4 fade-up" data-testid={`device-card-${d.name}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-md grid place-items-center bg-[#07090E] border border-[#222D3D]"><Icon className="h-5 w-5 text-slate-300" /></div>
          <div className="min-w-0">
            <p className="text-slate-100 font-medium truncate">{d.name}</p>
            <p className="text-xs text-slate-500">{d.hardware === "ds1" ? "DS-1 · RK3588" : "Existing hardware"}{d.agent_version && ` · agent ${d.agent_version}`}</p>
          </div>
        </div>
        <span data-testid={`device-status-${d.name}`} className={`flex items-center gap-1.5 font-mono text-[11px] px-2 py-0.5 rounded border ${online ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-slate-700 text-slate-400"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${online ? "bg-emerald-400 pulse-dot" : "bg-slate-500"}`} />{online ? "online" : d.last_seen_at ? "offline" : "never connected"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><p className="text-slate-500 uppercase tracking-[0.14em] text-[10px]">Arrivals sent</p><p className="font-mono text-lg text-slate-100" data-testid={`device-arrivals-${d.name}`}>{d.arrivals}</p></div>
        <div><p className="text-slate-500 uppercase tracking-[0.14em] text-[10px]">Last seen</p><p className="font-mono text-slate-300 mt-1">{fmtTime(d.last_seen_at)}</p></div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" className="border-[#222D3D] bg-transparent" onClick={onRotate} data-testid={`device-rotate-btn-${d.name}`}><KeyRound className="h-3.5 w-3.5 mr-1.5" />New key</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" data-testid={`device-remove-btn-${d.name}`}><Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0F141C] border-[#222D3D]">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {d.name}?</AlertDialogTitle>
              <AlertDialogDescription>Its key stops working immediately. Arrivals it already logged stay in the ledger.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="device-remove-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onRemove} className="bg-rose-500 hover:bg-rose-400" data-testid="device-remove-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
